import classnames from 'classnames';
import values from 'lodash/fp/values';
import Config from 'es6/core/config';
import { APP_IMAGE_PLACEHOLDER } from 'es6/core/styles/colors';
import React from 'react';
import { getTranscodedUrl } from 'es6/services/image-manager';
import { StyleSheet, css } from 'aphrodite';
import assetPath from 'es6/services/asset-path';
import { optionalOneOf } from 'es6/helpers/prop-types';

export const TranscodedImageSizes = Object.freeze({
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  APP_ITEM: 'appItem',
  APP_SENTENCE: 'appSentence',
  APP_SENTENCE_SMALL: 'appSentenceSmall',
});

export const TranscodedImagePlaceholders = Object.freeze({
  NONE: 'noPlaceholder',
  APP: 'bluePlaceholder',
  DEFAULT: 'defaultPlaceholder',
});

/**
 * These are the retina sizes. Use CSS to adjust the actual sizes displayed.
 */
const transcodingDimensions = Object.freeze({
  small:  Config.transcodingSettings.smallImage,
  medium: Config.transcodingSettings.mediumImage,
  large:  Config.transcodingSettings.largeImage,
  appItem: Config.transcodingSettings.appItemImage,
  appSentence: Config.transcodingSettings.appSentenceImage,
  // Weird case where the trancoded image is same size but the
  // displayed (CSS) size is smaller, keeping only for backwards
  // compatibility
  appSentenceSmall: Config.transcodingSettings.appSentenceImage,
});

/**
 * Passing in no src will show a placeholder.
 *
 * Placeholders can be picked from TranscodedImagePlaceholders enum.
 */
export function TranscodedImage({
  className = '',
  placeholder = TranscodedImagePlaceholders.DEFAULT,
  size = TranscodedImageSizes.SMALL,
  src = null,
}) {
  const classes = classnames(
    css(styles.image),
    css(styles[size]),
    // Insert a placeholder if source is not defined
    { [css(styles[placeholder])]: !src },
    // Abitrary className addition, kept for backward-compat with some
    // components that add class names for layouting
    className
  );

  const style = src ? transcodedImageStyle(src, size) : null;

  return <div style={style} className={classes} />;
}

// Add a default export so this component can also be imported directly
export default TranscodedImage;

TranscodedImage.propTypes = {
  className: React.PropTypes.string,
  src: React.PropTypes.string,
  size: React.PropTypes.oneOf(values(TranscodedImageSizes)).isRequired,
  placeholder: optionalOneOf(values(TranscodedImagePlaceholders)),
};

function transcodedImageStyle(src, size) {
  const url = getTranscodedUrl(src, transcodingDimensions[size]);
  return { backgroundImage: `url('${url}')` };
}

const styles = StyleSheet.create({
  image: {
    display: 'inline-block',
    borderRadius: '5px',
    backgroundSize: 'cover',
    backgroundPosition: '50% 50%',
  },
  small: halfSizeInPixes(transcodingDimensions.small),
  medium: halfSizeInPixes(transcodingDimensions.medium),
  large: halfSizeInPixes(transcodingDimensions.large),
  appItem: halfSizeInPixes(transcodingDimensions.appItem),
  appSentence: halfSizeInPixes(transcodingDimensions.appSentence),
  appSentenceSmall: {
    width: '150px',
    height: '138px',
  },
  defaultPlaceholder: {
    backgroundImage: `url(${assetPath('default/big/goal.jpg')})`,
  },
  bluePlaceholder: {
    backgroundColor: APP_IMAGE_PLACEHOLDER,
  },
});

function halfSizeInPixes(size) {
  return {
    width: `${size.width / 2}px`,
    height: `${size.height / 2}px`,
  };
}
